/* eslint-disable no-console */
import { Injectable, OnDestroy } from '@angular/core';
import {
  SwUpdate,
  VersionEvent,
  VersionReadyEvent,
} from '@angular/service-worker';
import { BehaviorSubject, map } from 'rxjs';

export type OfflineCacheStatus =
  | 'unsupported'
  | 'disabled'
  | 'not_registered'
  | 'installing'
  | 'activating'
  | 'ready'
  | 'failed'
  | 'update_available';

const STATUS_LABELS: Record<OfflineCacheStatus, string> = {
  ready: 'Ready',
  installing: 'Installing...',
  activating: 'Activating...',
  not_registered: 'Not installed',
  failed: 'Installation failed',
  disabled: 'Not available (dev build)',
  unsupported: 'Not supported',
  update_available: 'Update available',
};

const POLL_INTERVAL_MS = 2000;
const INSTALL_TIMEOUT_MS = 60000;

@Injectable({
  providedIn: 'root',
})
export class ServiceWorkerService implements OnDestroy {
  private readonly _status = new BehaviorSubject<OfflineCacheStatus>('not_registered');
  readonly status$ = this._status.asObservable();
  readonly statusLabel$ = this.status$.pipe(
    map((status) => STATUS_LABELS[status])
  );

  private pollIntervalId?: ReturnType<typeof setInterval>;
  private installTimeoutId?: ReturnType<typeof setTimeout>;
  private installStartedAt?: number;
  private trackedWorkers = new WeakSet<ServiceWorker>();
  private isActivatingUpdate = false;

  constructor(private swUpdate: SwUpdate) {
    this.initializeStatusTracking();
    this.checkForUpdates();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.clearInstallTimeout();
  }

  get status(): OfflineCacheStatus {
    return this._status.value;
  }

  get statusLabel(): string {
    return STATUS_LABELS[this._status.value];
  }

  private initializeStatusTracking(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (!('serviceWorker' in navigator)) {
      this.setStatus('unsupported');
      return;
    }

    if (!this.swUpdate.isEnabled) {
      this.setStatus('disabled');
      return;
    }

    void this.refreshRegistrationStatus();

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      void this.refreshRegistrationStatus();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void this.refreshRegistrationStatus();
        void this.swUpdate.checkForUpdate();
      }
    });

    this.startPollingIfNeeded();
  }

  private checkForUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    void this.swUpdate.checkForUpdate();

    this.swUpdate.versionUpdates.subscribe((event: VersionEvent) => {
      if (event.type === 'VERSION_READY') {
        const readyEvent = event as VersionReadyEvent;
        console.log(
          `A new version is available: ${readyEvent.currentVersion} -> ${readyEvent.latestVersion}`
        );
        void this.activateLatestVersion();
      }

      if (event.type === 'VERSION_INSTALLATION_FAILED') {
        this.setStatus('failed');
      }
    });
  }

  private async activateLatestVersion(): Promise<void> {
    if (this.isActivatingUpdate) {
      return;
    }
    this.isActivatingUpdate = true;

    try {
      const activated = await this.swUpdate.activateUpdate();
      if (activated && typeof document !== 'undefined') {
        document.location.reload();
      }
    } catch (error) {
      this.isActivatingUpdate = false;
      console.error('Could not activate the new app version', error);
      void this.refreshRegistrationStatus();
    }
  }

  private async refreshRegistrationStatus(): Promise<void> {
    if (!('serviceWorker' in navigator) || !this.swUpdate.isEnabled) {
      return;
    }

    const registration = await navigator.serviceWorker.getRegistration();

    if (!registration) {
      this.setStatus('not_registered');
      return;
    }

    if (registration.waiting) {
      this.trackWorkerState(registration.waiting);
    }

    if (registration.installing) {
      this.trackWorkerState(registration.installing);
    }

    if (registration.active) {
      this.trackWorkerState(registration.active);
    }

    this.setStatus(this.resolveStatusFromRegistration(registration));
  }

  private resolveStatusFromRegistration(
    registration: ServiceWorkerRegistration
  ): OfflineCacheStatus {
    if (registration.waiting && registration.active) {
      return 'update_available';
    }

    const installingState = registration.installing?.state;
    if (installingState === 'installing' || installingState === 'installed') {
      return installingState === 'installed' ? 'activating' : 'installing';
    }

    const activeState = registration.active?.state;
    if (activeState === 'activating') {
      return 'activating';
    }

    if (
      navigator.serviceWorker.controller &&
      activeState === 'activated'
    ) {
      return 'ready';
    }

    if (activeState === 'activated') {
      return 'ready';
    }

    return 'not_registered';
  }

  private trackWorkerState(worker: ServiceWorker): void {
    if (this.trackedWorkers.has(worker)) {
      return;
    }

    this.trackedWorkers.add(worker);
    worker.addEventListener('statechange', () => {
      void this.refreshRegistrationStatus();
    });
  }

  private setStatus(status: OfflineCacheStatus): void {
    if (this._status.value === status) {
      this.updatePollingForStatus(status);
      return;
    }

    this._status.next(status);
    this.updatePollingForStatus(status);
  }

  private updatePollingForStatus(status: OfflineCacheStatus): void {
    const pendingStatuses: OfflineCacheStatus[] = [
      'not_registered',
      'installing',
      'activating',
    ];

    if (pendingStatuses.includes(status)) {
      if (!this.installStartedAt) {
        this.installStartedAt = Date.now();
        this.installTimeoutId = setTimeout(() => {
          if (pendingStatuses.includes(this._status.value)) {
            this.setStatus('failed');
          }
        }, INSTALL_TIMEOUT_MS);
      }
      this.startPollingIfNeeded();
      return;
    }

    this.installStartedAt = undefined;
    this.clearInstallTimeout();
    this.stopPolling();
  }

  private startPollingIfNeeded(): void {
    if (this.pollIntervalId) {
      return;
    }

    this.pollIntervalId = setInterval(() => {
      void this.refreshRegistrationStatus();
    }, POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = undefined;
    }
  }

  private clearInstallTimeout(): void {
    if (this.installTimeoutId) {
      clearTimeout(this.installTimeoutId);
      this.installTimeoutId = undefined;
    }
  }

  public verifyServiceWorkerRegistration(): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        if (registrations.length) {
          console.log('Service Worker registered:', registrations);
        } else {
          console.log('No Service Workers registered.');
        }
      });
    } else {
      console.log('Service Workers are not supported in this browser.');
    }
  }
}
